/**
 * Light Palette Definition
 *
 * Defined Sections:
 * - PRIMARY BRAND (Interactive)
 * - NEUTRAL & SEMANTIC
 * - DECORATIVE / ACCENT
 * - CATEGORY TAB PILL BACKGROUNDS
 * - STATUS BADGES
 * - PILLS / BADGES
 * - INLINE FORM-VALIDATION MESSAGE TEXT
 * - INLINE OTP FEEDBACK BANNER (RewampFeedbackBanner, Figma 520-4764/4894)
 * - SEGMENTED TABS
 * - SELECTABLE OPTION CARD
 * - DATA TABLE
 * - APPLICATION STATUS TIMELINE
 * - WEATHER ADVISORY (IMD alert levels)
 * - ACCESSIBILITY DIALOG
 * - OVERLAYS (on imagery)
 * - BACKGROUNDS
 * - TEXT
 * - FORM TEXT
 * - DIVIDER
 * - ICON
 * - BUTTON COMPATIBILITY KEYS
 * - CHART COLORS
 * - SPACING TOKENS (Base: 4px)
 */
const OVERLAY_SCRIM = "rgba(0,0,0,0.3)";
// Shared base values: one hex, several roles (novelty gate).
const SOFT_GREEN_STROKE = "#B0D0C0";
const MUTED_INK = "#696969";
const DARK_INK = "#11151E";
const DEEP_GREEN = "#004D26";

export const getPaletteLight = () => {
  return {
  primary: {
    main: "#006633",
    dark: "#004D26",
    light: "#E5F0EB",
    chipActive: "#2D5E32",
    interactive: "#006633",
    interactiveHover: "#004D26",
    softSurface: "#E5F0EB",
  },

  neutral: {
    pageBg: "#F5F2EB",
    surface: "#FFFFFF",
    headings: "#11151E",
    body: "#5C6460",
    stroke: "#E0E0D8",
    searchStroke: SOFT_GREEN_STROKE,
    navInactive: MUTED_INK,
    nonInteractive: "#A75335",
    locationPin: "#7A5038",
    decoratedText: "#B85C3A",
    disabled: "#A2ABA6",
    iconMuted: "#9F9F9F",
    placeholder: "#868686",
    placeholderOnGreen: "#95D5B2",
    warmHeadings: "#2C1810",
    mutedText: "#9A9080",
    warmBodyText: "#5C4A3D",
    warmSurface: "#F8ECE4",
    chipBg: "#F0EFEB",
    warmStroke: "rgba(44,24,16,0.06)",
    tickerBg: "#EDF7F0",
    tickerBorder: "#F5E6C8",
    disabledSurface: "#FAFAFA",
    legacySeedFormBg: "#F0F5F2",
    springGoldSurface: "#FEF9ED", // call-out band, e.g. KCC banner
    springGold200: "#FDF5E4", // success-dialog reference block
  },

  accent: {
    butterscotch: "#D08C2A",
    butterscotchBg: "#FAEBC7",
    butterscotchDark: "#7A5B10", // badge text on butterscotchBg: 5.3:1 (develop's #8B6914 was 4.3:1)
    butterscotchDivider: "#D5D5D5", // item separator inside a butterscotchBg badge
    butterscotchStroke: "#F0BE49",
    butterscotchOnGreen: "#FAEBC7",
    // Ink on solid butterscotch (LIVE badge): 6.49:1; light headings in dark mode would be 2.55:1.
    onButterscotch: DARK_INK,
    liveBandText: DEEP_GREEN,
    wildLime: "#7FC241",
    informativeIcon: "#A75335",
  },

  categoryTabBg: {
    green: "#ECF6E3",
    yellow: "#FFF9E0",
    grey: "#F7F7F7",
    blue: "#E8F2FF",
    peach: "#FFEEE0",
    purple: "#F0ECFF",
  },

  status: {
    open: { bg: "#7FC241", text: "#1C211E" },
    new: { bg: "#EFD9B8", text: "#1C211E" },
    closed: { bg: "#C0392B", text: "#FFFFFF" },
    alertRed: "#B82B2B",
    alertOrange: "#D46B00",
    alertYellow: "#E8B400",
    alertGreen: "#00ff00",
  },

  pills: {
    info: { bg: "#ECF8FF", text: "#035D94" },
    success: { bg: "#ECF6E3", text: "#247B12" },
    danger: { bg: "#C0392B", text: "#FFFFFF" },
    error: { bg: "#FFDDDD", text: "#AE100F" },
    applied: { bg: "#ECF6E3", text: "#247B12" },
    neutral: { bg: "#EFECEC", text: "#5C6460", border: "#727272" },
    closed: { bg: "#EFECEC", text: "#727272" },
    prefilled: { bg: "#E5F0EB", text: "#006633" }
  },

  formMessage: {
    success: "#1B6B3A",
    warning: "#8A5A00",
    error: "#C0392B",
  },

  feedbackBanner: {
    warningBg: "#FFF8E1",
    warning: "#8B6914",
    errorBg: "#FDECEA",
    error: "#C0392B",
  },

  tab: {
    activeBg: "#2D5E32",
    activeText: "#FFFFFF",
    activeBadgeBg: "#E5F0EB",
    inactiveBg: "rgba(181,171,148,0.16)",
    inactiveBorder: "#E2DDD1",
    inactiveText: "#11151E",
    inactiveBrandText: "#2D5E32",
  },

  optionCard: {
    unselectedBg: "#FAFBFB",
    unselectedBorder: SOFT_GREEN_STROKE,
    selectedBg: "rgba(0,102,51,0.1)",
    selectedShadow: "0px 2px 12px rgba(0,102,51,0.2)",
    glyph: "#002E17",
  },

  table: {
    headerBg: "#F3F3F3",
    rowBg: "#FFFFFF",
    rowAltBg: "#FAFAFA",
    border: "#E2DDD1",
  },

  statusTimeline: {
    current: "#90722C",
    iconSize: 28,
    successIcon: {
      bg: "#BED2A7",
      markSize: 18,
    },
    connector: {
      width: 2,
      dash: 2,
      gap: 4,
      gradientStart: "32.98%",
      gradientEnd: "100.49%",
      pending: { start: "#6C5521" },
      success: { start: "#5F9231", end: "#E9E9E9" },
    },
    stepContent: {
      lineOffset: 1.625,
      height: 7,
      typography: {
        fontWeight: 500,
        fontSize: "0.75rem",
        lineHeight: "16px",
      },
    },
    stepInnerGap: 1,
    stepGap: 2.5,
    stepLabel: {
      iconGap: 2,
      typography: {
        fontWeight: 600,
        fontSize: "0.875rem",
        lineHeight: "18px",
        paddingBottom: 0.125,
      },
    },
    card: {
      borderRadius: 2,
      padding: 2,
      titleMarginBottom: 1.5,
    },
    stageCard: {
      accentBgOpacity: 0.1,
      dividerWidth: "0.8px",
      dotSize: 1,
      bandPaddingY: 1.5,
      cellPaddingY: 2,
      cellPaddingX: 2.5,
      cellGap: 1.75,
      iconSize: 4.5,
      fieldGap: 0.75,
    },
  },

  weatherAdvisory: {
    // Same in dark mode — dark.js inherits these unchanged.
    level1: { rightBg: "#55ADC7", text: "#11151E", textMuted: "#11151E" },
    level2: { rightBg: "#E8BE37", text: "#11151E", textMuted: "#11151E" },
    level3: { rightBg: "#C0722C", text: "#11151E", textMuted: "#11151E" },
    level4: { rightBg: "#B82B2B", text: "#FFFFFF", textMuted: "#FFFFFF" },
  },

  accessibility: {
    bodyBg: "#F5F5FA",
    tileBg: "#FFFFFF",
    tileText: DARK_INK,
    shortcutText: "#E0A84A",
    resetBg: "#EDF7F0",
    resetBorder: "#95D5B2",
    resetText: "#1B4332",
    footerDivider: "#E8E8F0",
    metaText: "#999999",
    metaBrand: "#2D6A4F",
    // Sits on fixed butterscotch, so it does not flip in dark mode.
    langActiveText: "#0B2118",
  },

  overlay: {
    onImageControl: "rgba(255,255,255,0.85)",
    onImageSkeleton: "rgba(255,255,255,0.2)",
    scrim: OVERLAY_SCRIM,
    scrimStrong: "rgba(0,0,0,0.6)",
  },

  background: {
    default: "#F5F2EB",
    paper: "#FFFFFF",
    tint: "#FDFBF8", // theme-new: user-approved PWA page background (2026-10-21)
    primaryGreen: "#006633",
    tertiaryGreen: "#E5F0EB",
    white: "#FFFFFF",
    tertiaryWhite: "#F5F2EB",
    butterscotch: "#D08C2A",
    contactCardGradient: "linear-gradient(270deg, #FFFFFF 0%, #E5F0EB 100%)",
    appDownloadGradient:
      "linear-gradient(145.47deg, rgba(237,247,240,1) 0%, rgba(255,255,255,1) 50%, rgba(216,243,220,1) 100%)",
    // public/splash.html repeats this value; keep both in sync.
    splashGradient: "linear-gradient(179.87deg, #C1E8F0 -24.03%, #D5EEF4 6.93%, #ECF6F9 35.5%)",
  },

  text: {
    primary: "#11151E",
    secondary: "#006633",
    disabled: "#A2ABA6",
    requiredMark: "#B42318",
    white: "#FFFFFF",
    textGrey: "#5C6460",
    textGreen: "#006633",
    yellow: "#D08C2A",
    greyGreen: "#E5F0EB",
    darkGreyGreen: "#5C6460",
    newsDate: "#5C6460",
    error: "#C0392B",
    success: "#7FC241",
    warning: "#D08C2A",
  },

  formText: {
    label: "#5C6460",
    placeholder: "#717680",
    input: "#11151E",
    checkbox: "#2F2F2F",
  },

  dialog: {
    title: "#2F2F2F",
    description: MUTED_INK,
  },

  divider: "#E0E0D8",

  icon: {
    faqIcon: "#E5F0EB",
    nonInteractive: "#A75335",
  },

  button: {
    primaryBackground: "#006633",
    primaryColor: "#FFFFFF",
    primaryHoverBackground: DEEP_GREEN,
    secondaryBackground: "#FFFFFF",
    secondaryColor: "#006633",
    secondaryBorder: "#006633",
    secondaryHoverBackground: "rgba(0, 102, 51, 0.1)",
    secondaryHoverColor: "#004D26",
    disabledBackground: "#9C9C9C",
    disabledColor: "#454545",
    greenDisabledBackground: "rgba(0,102,51,0.5)",
    greenDisabledColor: "#FBFDFC",
    dangerBackground: "#A5292B",
    dangerColor: "#FFFFFF",
    dangerHoverBackground: "#8C2224",
  },

  chart: {
    colors: [
      "#004D26",
      "#006633",
      "#7FC241",
      "#E5F0EB",
      "#D08C2A",
      "#FAEBC7",
      "#A75335",
      "#5C6460",
      "#11151E",
      "#E0E0D8",
    ],
    heatMapMin: "#E5F0EB",
    heatMapMax: "#006633",
  },

  spacingTokens: {
    xs: "4px",
    sm: "8px",
    "md-sm": "12px",
    md: "16px",
    "md-lg": "20px",
    lg: "24px",
    xl: "32px",
    "2xl": "48px",
    "3xl": "64px",
  },

  // White-alpha overlays used on the green nav strip (same in both modes — the strip stays green).
  onGreen: {
    hoverBg: "rgba(255,255,255,0.1)",
    hoverBgStrong: "rgba(255,255,255,0.12)",
    menuBorder: "rgba(255,255,255,0.12)",
    menuShadow: "0 8px 24px rgba(0,0,0,0.24)",
    fieldBg: "rgba(255,255,255,0.06)",
    fieldBgHover: "rgba(255,255,255,0.18)",
    searchBg: "rgba(255,255,255,0.08)",
    searchBgHover: "rgba(255,255,255,0.15)",
    border: "rgba(255,255,255,0.4)",
    borderStrong: "rgba(255,255,255,0.6)",
    divider: "rgba(255,255,255,0.2)",
    focusRing: "0 0 0 2px rgba(208, 140, 42, 0.4)",
  },
  layout: {
    pagePadding: { xs: 2, sm: 4, md: 9 },
    pagePaddingTop: { xs: 2, md: 3 },
    pagePaddingBottom: { xs: 5, md: 7.5, lg: 10 },
    cardPadding: { xs: 2, sm: 4 },
    sectionGap: 3,
    onboardingCardMaxWidth: "460px",
    cardShadow: "0px 2px 6px rgba(0, 0, 0, 0.04)",
    softCardShadow: "0px 2px 12px rgba(0, 0, 0, 0.04)",
    dropdownShadow: "-9px -3px 15px rgba(0,46,23,0.16), 13px 16px 15.7px rgba(0,46,23,0.16)",
    selectedShadow: "0px 2px 12px rgba(0, 102, 51, 0.2)",
  },

  controls: {
    scrollArrowSize: 4.5,
    scrollArrowGutter: 4.5,
  },

  aqi: {
    // AQI wheel: progress gradient stops (good → severe) and the unfilled track.
    gauge: { good: "#006633", fair: "#72AF3B", poor: "#F0BE49", severe: "#AE100F", track: "#C8C8C8" },
    surface: "#F3F6F6",
    text: "#7A8A8A",
    heading: "#030808",
    divider: "#E0E0E0",
  },
};
};
